import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

type Step = 'businessDetails' | 'settings' | 'serviceCreated' | 'addServices';

// Add this function to handle auth errors
const handleAuthError = (error: any, navigate: any, toast: any) => {
  console.error('Authentication error during onboarding:', error);
  
  // Clear auth tokens
  localStorage.removeItem('supabase.auth.token');
  localStorage.removeItem('supabase.auth.token.access_token');
  localStorage.removeItem('supabase.auth.token.refresh_token');
  localStorage.removeItem('supabase.auth.expires_at');
  localStorage.removeItem('supabase.auth.expires_in');
  
  // Notify user
  toast({
    variant: "destructive",
    title: "Authentication Error",
    description: "Please sign in again to continue.",
  });
  
  // Redirect to auth page
  navigate("/auth");
};

export const useOnboarding = () => {
  const [currentStep, setCurrentStep] = useState<Step>('businessDetails');
  const [isLoading, setIsLoading] = useState(false);
  const [businessDetails, setBusinessDetails] = useState({
    profession: '',
    serviceType: '',
    referralSource: '',
    country: 'Kenya',
    preferredMessaging: 'whatsapp',
  });
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [isGeneratingName, setIsGeneratingName] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [connectingTelegram, setConnectingTelegram] = useState(false);
  const [telegramConnectionAttempted, setTelegramConnectionAttempted] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Check authentication on mount
    const checkAuth = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error || !user) {
          handleAuthError(error || new Error('No user found'), navigate, toast);
          return;
        }
      } catch (error) {
        handleAuthError(error, navigate, toast);
      }
    };
    
    checkAuth();
  }, [navigate, toast]);

  const handleBusinessDetailsSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) {
        console.error("Form element is not an HTMLFormElement");
        setIsLoading(false);
        toast({
          variant: "destructive",
          title: "Error",
          description: "There was a problem with the form submission. Please try again.",
        });
        return;
      }
      
      const formData = new FormData(form);
      const selectedProf = formData.get("profession") as string;
      const customProf = formData.get("customProfession") as string;
      const serviceType = formData.get("serviceType") as string;
      const referralSource = formData.get("referralSource") as string;
      const firstName = formData.get("firstName") as string;
      const lastName = formData.get("lastName") as string;
      const country = formData.get("country") as string;
      const preferredMessaging = formData.get("preferredMessaging") as string;
      
      const profession = selectedProf === "Other" ? customProf : selectedProf;

      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        handleAuthError(authError || new Error('No user found'), navigate, toast);
        return;
      }

      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            profession: profession,
            service_type: serviceType,
            referral_source: referralSource,
            company_name: profession,
            first_name: firstName,
            last_name: lastName,
            country: country,
            preferred_messaging: preferredMessaging,
          })
          .eq('id', user.id);

        if (error) throw error;

        setBusinessDetails({ 
          profession, 
          serviceType, 
          referralSource, 
          country,
          preferredMessaging, 
        });
        setCurrentStep('settings');
      } catch (error) {
        console.error("Business details error:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save your business details. Please try again.",
        });
      }
    } catch (e) {
      console.error("Unexpected error in handleBusinessDetailsSubmit:", e);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsSubmit = async (phonePrefix: string, phoneNumber: string | null, businessName: string) => {
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      if (!businessName || businessName.trim() === "") {
        throw new Error('Business name is required');
      }

      const fullWhatsappNumber = phoneNumber ? 
        `${phonePrefix}${phoneNumber.replace(/^0+/, '')}` : 
        null;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          company_name: businessName.trim(),
          whatsapp_number: fullWhatsappNumber,
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }

      // Check preferred messaging platform and redirect accordingly
      if (businessDetails.preferredMessaging === 'telegram') {
        // If telegram, go to telegram connect page
        navigate('/telegram-connect');
      } else {
        // For WhatsApp or other platforms, skip telegram connection and go to service created
        setCurrentStep('serviceCreated');
      }
    } catch (error) {
      console.error('Settings update error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Could not update your settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setTempImageUrl(URL.createObjectURL(file));
      setShowCropDialog(true);
    }
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    try {
      setIsLoading(true);
      setShowCropDialog(false);

      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user found');

      const fileName = `${user.id}-${Date.now()}.jpg`;
      const { error: uploadError, data } = await supabase.storage
        .from('profiles')
        .upload(`profile-images/${fileName}`, blob, {
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profiles')
        .getPublicUrl(`profile-images/${fileName}`);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_image_url: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfileImage(publicUrl);
      setTempImageUrl(null);
      
      toast({
        title: "Success",
        description: "Profile image updated successfully",
      });
    } catch (error) {
      console.error('Image upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateBusinessName = async () => {
    setIsGeneratingName(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setBusinessName('');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, profession, country')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      console.log('Invoking generate-business-name function with:', {
        firstName: profile?.first_name || '',
        lastName: profile?.last_name || '',
        profession: businessDetails.profession || profile?.profession || '',
        country: businessDetails.country || profile?.country || 'Kenya',
      });

      const { data, error } = await supabase.functions.invoke('generate-business-name', {
        body: {
          firstName: profile?.first_name || '',
          lastName: profile?.last_name || '',
          profession: businessDetails.profession || profile?.profession || '',
          country: businessDetails.country || profile?.country || 'Kenya',
        },
      });

      if (error) {
        console.error('Error from function:', error);
        throw error;
      }

      console.log('Business name generation response:', data);
      setBusinessName(data.businessName);
    } catch (error) {
      console.error('Error generating business name:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate business name. Please try again.",
      });
    } finally {
      setIsGeneratingName(false);
    }
  };

  const connectTelegram = async () => {
    setConnectingTelegram(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      
      const { data, error } = await supabase.functions.invoke('telegram-bot', {
        body: { 
          userId: user.id,
          pagePath: location.pathname,
          action: "connect_telegram",
          timestamp: new Date().toISOString(),
          userProfile: await getUserProfile(user.id)
        }
      });
      
      if (error) throw error;
      
      if (data.deeplink) {
        window.open(data.deeplink, '_blank');
        setTelegramConnectionAttempted(true);
        toast({
          title: "Telegram Bot",
          description: "Please check your browser for the Telegram bot link and complete the connection by sending /start",
        });
      }
    } catch (error) {
      console.error('Error connecting to Telegram:', error);
      toast({
        title: "Error",
        description: "Failed to connect to Telegram. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setConnectingTelegram(false);
    }
  };

  const getUserProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      return data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const skipTelegramConnect = () => {
    setCurrentStep('serviceCreated');
  };

  const handleBackStep = () => {
    if (currentStep === 'settings') {
      setCurrentStep('businessDetails');
    } else if (currentStep === 'serviceCreated') {
      setCurrentStep('settings');
    } else if (currentStep === 'addServices') {
      setCurrentStep('serviceCreated');
    }
  };

  return {
    currentStep,
    isLoading,
    businessDetails,
    profileImage,
    tempImageUrl,
    showCropDialog,
    isGeneratingName,
    businessName,
    connectingTelegram,
    telegramConnectionAttempted,
    setBusinessName,
    setTempImageUrl,
    setShowCropDialog,
    handleBusinessDetailsSubmit,
    handleSettingsSubmit,
    handleImageUpload,
    handleCropComplete,
    generateBusinessName,
    connectTelegram,
    skipTelegramConnect,
    handleBackStep,
    navigate
  };
};
